import React, { useEffect, useRef, useState, useCallback } from "react";
import { Upload, Search, X as XIcon, Trash2, Check, Image as ImageIcon, Edit3 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { uploadFilesInBatches } from "@/lib/uploadQueue";

/**
 * Modal Media Library / Image Picker.
 * Props:
 *   onClose:    () => void
 *   onSelect:   (media) => void   — called with the picked media object (has .url, .urls, .id, .alt, .title)
 *   multi:      bool              — if true, "Select" button picks N at once via array
 */
export default function MediaPicker({ onClose, onSelect, multi = false }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [uploading, setUploading] = useState(false);
  const [queue, setQueue] = useState([]); // [{id, name, size, pct, status: 'pending'|'uploading'|'done'|'error', error?}]
  const [selected, setSelected] = useState([]); // ids
  const [editing, setEditing] = useState(null);
  const dropRef = useRef(null);
  const fileRef = useRef(null);

  const load = useCallback(async (query = "") => {
    try {
      const r = await api.get("/admin/media", { params: { q: query, limit: 200 } });
      setItems(r.data.items);
      setTotal(r.data.total);
    } catch (e) { toast.error("Could not load media"); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const t = setTimeout(() => load(q), 250);
    return () => clearTimeout(t);
  }, [q, load]);

  const handleFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    // Initialise the visible queue (so user sees N pending bars right away)
    const initialQueue = files.map((f, i) => ({
      id: i, name: f.name, size: f.size, pct: 0, status: "pending",
    }));
    setQueue(initialQueue);

    const { ok, fail } = await uploadFilesInBatches({
      files,
      concurrency: 3,
      maxRetries: 2,
      onProgress: (id, pct) => setQueue(prev => prev.map(q2 =>
        q2.id === id ? { ...q2, pct, status: pct >= 100 ? "done" : "uploading" } : q2)),
      onItemDone: (id) => {
        setQueue(prev => prev.map(q2 => q2.id === id ? { ...q2, pct: 100, status: "done" } : q2));
        load(q); // refresh grid the moment each file finishes — feels much snappier
      },
      onItemFail: (id, err) => setQueue(prev => prev.map(q2 =>
        q2.id === id ? { ...q2, status: "error", error: err?.userMessage || err?.response?.data?.detail || err?.message || "Upload failed" } : q2)),
    });

    setUploading(false);
    if (ok) toast.success(`Uploaded ${ok} image${ok === 1 ? "" : "s"}`);
    if (fail) toast.error(`${fail} upload${fail === 1 ? "" : "s"} failed — see details below`);
    // Clear the queue after a short delay so users can see the final state, unless there are errors
    if (!fail) setTimeout(() => setQueue([]), 1500);
  };

  // Drag & drop
  useEffect(() => {
    const el = dropRef.current;
    if (!el) return;
    const prevent = (e) => { e.preventDefault(); e.stopPropagation(); };
    const onDrop = (e) => {
      prevent(e);
      el.classList.remove("ring-2", "ring-[#D4AF37]");
      handleFiles(Array.from(e.dataTransfer?.files || []).filter(f => f.type.startsWith("image/")));
    };
    const onOver = (e) => { prevent(e); el.classList.add("ring-2", "ring-[#D4AF37]"); };
    const onLeave = (e) => { prevent(e); el.classList.remove("ring-2", "ring-[#D4AF37]"); };
    el.addEventListener("dragover", onOver);
    el.addEventListener("dragleave", onLeave);
    el.addEventListener("drop", onDrop);
    return () => {
      el.removeEventListener("dragover", onOver);
      el.removeEventListener("dragleave", onLeave);
      el.removeEventListener("drop", onDrop);
    };
  }, [q]);

  const toggle = (m) => {
    if (multi) {
      setSelected(s => s.includes(m.id) ? s.filter(x => x !== m.id) : [...s, m.id]);
    } else {
      onSelect(m);
      onClose();
    }
  };

  const confirmMulti = () => {
    const picked = items.filter(i => selected.includes(i.id));
    onSelect(picked);
    onClose();
  };

  const remove = async (m, ev) => {
    ev.stopPropagation();
    if (!window.confirm(`Delete "${m.title || m.original_filename}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/media/${m.id}`);
      toast.success("Deleted");
      load(q);
    } catch { toast.error("Could not delete"); }
  };

  const saveEdit = async () => {
    try {
      await api.put(`/admin/media/${editing.id}`, { title: editing.title, alt: editing.alt });
      toast.success("Saved");
      setEditing(null);
      load(q);
    } catch { toast.error("Could not save"); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/85 backdrop-blur flex items-center justify-center p-4" data-testid="media-picker">
      <div className="bg-[#0a0a0a] border border-white/15 w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div>
            <div className="eyebrow">Media Library</div>
            <h3 className="font-serif text-2xl">Pick an image</h3>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white p-2" data-testid="media-picker-close">
            <XIcon size={22}/>
          </button>
        </div>

        {/* Toolbar */}
        <div className="p-5 border-b border-white/10 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              type="text" placeholder="Search by title, alt, or filename..."
              value={q} onChange={e => setQ(e.target.value)} data-testid="media-search"
              className="w-full bg-black border border-white/15 pl-9 pr-3 py-2.5 text-sm focus:border-[#D4AF37] outline-none" />
          </div>
          <input type="file" multiple accept="image/*" ref={fileRef} onChange={e => handleFiles(Array.from(e.target.files || []))}
            className="hidden" data-testid="media-file-input" />
          <button onClick={() => fileRef.current?.click()} disabled={uploading} className="btn-gold" data-testid="media-upload-btn">
            <Upload size={14}/> {uploading ? "Uploading..." : "Upload"}
          </button>
        </div>

        {/* Upload queue — visible per-file progress with retry status */}
        {queue.length > 0 && (
          <div className="border-b border-white/10 px-5 py-3 space-y-2 max-h-48 overflow-y-auto" data-testid="upload-queue">
            {queue.map(q2 => (
              <div key={q2.id} className="flex items-center gap-3 text-xs">
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between gap-3 mb-1">
                    <span className="truncate text-white/80" title={q2.name}>{q2.name}</span>
                    <span className={`shrink-0 ${q2.status === "error" ? "text-red-400" : "text-white/40"}`}>
                      {q2.status === "error" ? "Failed" : q2.status === "done" ? "Done" : `${q2.pct}%`}
                    </span>
                  </div>
                  <div className="h-1 bg-white/10 overflow-hidden rounded-sm">
                    <div className={`h-full transition-all ${q2.status === "error" ? "bg-red-500" : "bg-[#D4AF37]"}`}
                         style={{ width: `${q2.status === "error" ? 100 : q2.pct}%` }} />
                  </div>
                  {q2.status === "error" && q2.error && (
                    <div className="text-[0.65rem] text-red-400/80 mt-1">{q2.error}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Drop zone wraps the grid */}
        <div ref={dropRef} className="flex-1 overflow-y-auto p-5 transition" data-testid="media-grid">
          {items.length === 0 ? (
            <div className="text-center text-white/40 py-20">
              <ImageIcon size={48} className="mx-auto mb-4 opacity-30"/>
              {q ? "No images match your search." : "No images yet. Drag files here or click Upload to add your first."}
            </div>
          ) : (
            <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {items.map(m => {
                const isSel = selected.includes(m.id);
                return (
                  <div key={m.id} onClick={() => toggle(m)} data-testid={`media-item-${m.id}`}
                    className={`relative cursor-pointer bg-black border ${isSel ? "border-[#D4AF37] ring-1 ring-[#D4AF37]" : "border-white/10 hover:border-white/30"} transition group`}>
                    <div className="aspect-square overflow-hidden">
                      <img src={m.thumb_url} alt={m.alt || ""} loading="lazy" className="w-full h-full object-cover" />
                    </div>
                    {isSel && (
                      <div className="absolute top-2 right-2 bg-[#D4AF37] text-black p-1.5"><Check size={12}/></div>
                    )}
                    <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition flex flex-col justify-end p-2">
                      <div className="text-xs text-white/90 truncate">{m.title || m.original_filename}</div>
                      <div className="flex gap-1 mt-1">
                        <button onClick={(e) => { e.stopPropagation(); setEditing({ ...m }); }}
                          className="p-1.5 bg-black/60 hover:bg-[#D4AF37] hover:text-black" title="Edit info"><Edit3 size={11}/></button>
                        <button onClick={(e) => remove(m, e)}
                          className="p-1.5 bg-black/60 hover:bg-red-500" data-testid={`media-delete-${m.id}`} title="Delete"><Trash2 size={11}/></button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-white/10 flex items-center justify-between gap-4">
          <div className="text-xs text-white/40">{total} image{total === 1 ? "" : "s"} · Drag &amp; drop here to upload</div>
          {multi && (
            <button onClick={confirmMulti} disabled={!selected.length} className="btn-gold disabled:opacity-30" data-testid="media-pick-multi">
              Select {selected.length || ""} {selected.length === 1 ? "image" : "images"}
            </button>
          )}
        </div>
      </div>

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/85 z-[110] flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-[#0a0a0a] border border-white/15 max-w-lg w-full p-6" onClick={e => e.stopPropagation()}>
            <h4 className="font-serif text-xl mb-4">Edit Image Info</h4>
            <img src={editing.thumb_url} alt="" className="w-full aspect-video object-cover mb-4" />
            <label className="block mb-3">
              <span className="text-[0.6rem] tracking-[0.25em] uppercase text-white/60">Title</span>
              <input value={editing.title || ""} onChange={e => setEditing({...editing, title: e.target.value})}
                className="mt-1 w-full bg-black border border-white/15 px-3 py-2 text-sm focus:border-[#D4AF37] outline-none" />
            </label>
            <label className="block mb-5">
              <span className="text-[0.6rem] tracking-[0.25em] uppercase text-white/60">Alt Text (SEO)</span>
              <input value={editing.alt || ""} onChange={e => setEditing({...editing, alt: e.target.value})}
                placeholder="Bride walking at Casa Loma"
                className="mt-1 w-full bg-black border border-white/15 px-3 py-2 text-sm focus:border-[#D4AF37] outline-none" />
            </label>
            <div className="flex gap-3">
              <button onClick={saveEdit} className="btn-gold">Save</button>
              <button onClick={() => setEditing(null)} className="btn-ghost">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
